import { CalendarEventResponse } from '@troia-v3/types';

export interface EventColumnInfo {
  column: number;
  totalColumns: number;
}

/**
 * Assigns column positions to overlapping events (Google Calendar style).
 * Events that overlap in time are placed side-by-side in columns.
 *
 * @param events - Events for a single day
 * @param minVisualDurationMin - Minimum visual duration (minutes) for overlap detection.
 *   Events shorter than this are treated as if they last this long, preventing
 *   visual overlap caused by min-height rendering.
 */
export function assignEventColumns(
  events: CalendarEventResponse[],
  minVisualDurationMin: number = 30
): Map<string, EventColumnInfo> {
  const result = new Map<string, EventColumnInfo>();

  if (events.length === 0) return result;

  if (events.length === 1) {
    result.set(events[0].id, { column: 0, totalColumns: 1 });
    return result;
  }

  // Convert to minute-based intervals
  const items = events.map(e => {
    const start = new Date(e.startTime);
    const end = new Date(e.endTime);
    const startMin = start.getHours() * 60 + start.getMinutes();
    let endMin = end.getHours() * 60 + end.getMinutes();
    // Zero-duration events (activities/tasks) → show as 30-min card
    if (endMin === startMin) endMin = Math.min(startMin + 30, 24 * 60);
    // Events crossing midnight → extend to end of day
    else if (endMin < startMin) endMin = 24 * 60;
    return {
      id: e.id,
      startMin,
      endMin,
      // Effective end includes min visual duration to prevent visual overlap from min-height
      effectiveEndMin: Math.max(endMin, startMin + minVisualDurationMin),
    };
  });

  // Sort by start time, then by duration descending (longer events get first column)
  items.sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    return (b.endMin - b.startMin) - (a.endMin - a.startMin);
  });

  // Greedy column assignment: place each event in the first column where it fits
  const columnEnds: number[] = [];
  const eventCol = new Map<string, number>();

  for (const item of items) {
    let placed = false;
    for (let col = 0; col < columnEnds.length; col++) {
      if (columnEnds[col] <= item.startMin) {
        columnEnds[col] = item.effectiveEndMin;
        eventCol.set(item.id, col);
        placed = true;
        break;
      }
    }
    if (!placed) {
      eventCol.set(item.id, columnEnds.length);
      columnEnds.push(item.effectiveEndMin);
    }
  }

  // Build adjacency list for overlap detection (using effective end times)
  const adj = new Map<string, string[]>();
  for (const item of items) adj.set(item.id, []);

  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      if (items[i].startMin < items[j].effectiveEndMin && items[j].startMin < items[i].effectiveEndMin) {
        adj.get(items[i].id)!.push(items[j].id);
        adj.get(items[j].id)!.push(items[i].id);
      }
    }
  }

  // BFS to find connected components (overlap groups)
  const visited = new Set<string>();

  for (const item of items) {
    if (visited.has(item.id)) continue;

    const group: string[] = [];
    const queue = [item.id];
    visited.add(item.id);

    while (queue.length > 0) {
      const id = queue.shift()!;
      group.push(id);
      for (const neighbor of adj.get(id)!) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }

    // All events in this overlap group share the same total column count
    const totalColumns = Math.max(...group.map(id => eventCol.get(id)!)) + 1;
    for (const id of group) {
      result.set(id, { column: eventCol.get(id)!, totalColumns });
    }
  }

  return result;
}
